// r24 Product Catalog — Sworn Translator A5 product page renderer.
import ProductDepthBlock from "@/components/ProductDepthBlock";
import { Link } from "@tanstack/react-router";
import { Check, Clock, ShieldCheck, Languages, ArrowRight, FileCheck2, Stamp, Award, Gavel } from "lucide-react";
import type { SwornTranslatorProduct } from "@/data/sworn-translator-products";
import { SWORN_LEAD_TRANSLATOR, relatedSwornProducts, SWORN_TURNAROUND_EXPRESS } from "@/data/sworn-translator-products";
import SwornTranslatorProductCTA from "./SwornTranslatorProductCTA";
import { CrossSellStrip } from "@/components/CrossSellStrip";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";

interface Props {
  product: SwornTranslatorProduct;
  locale: "th" | "en";
  faqs: { q: string; a: string }[];
}

export default function SwornTranslatorProductPage({ product, locale, faqs }: Props) {
  const isTh = locale === "th";
  const name = isTh ? product.nameTh : product.nameEn;
  const catLabel = isTh ? product.categoryLabelTh : product.categoryLabelEn;
  const longDesc = isTh ? product.longDescTh : product.longDescEn;
  const bullets = isTh ? product.bulletTh : product.bulletEn;
  const steps = isTh ? product.processStepsTh : product.processStepsEn;
  const accepted = isTh ? product.acceptedByTh : product.acceptedByEn;
  const langs = isTh ? product.featuredLanguagesTh : product.featuredLanguagesEn;
  const related = relatedSwornProducts(product.slug, 6);

  const L = isTh ? {
    trust: `รับรองโดย ${SWORN_LEAD_TRANSLATOR} + เครือข่าย sworn translator ปลายทาง`,
    priceLabel: "ค่าบริการต่อชุด",
    turnLabel: "ระยะเวลา",
    acceptedBy: "ยอมรับโดย",
    langsTitle: "ภาษาที่รองรับ (สูงสุด)",
    usedFor: "ใช้ยื่นได้กับ",
    whatIncluded: "รวมในราคานี้",
    chainTitle: "Chain การรับรอง 4 ขั้น",
    tiers: "ทางเลือกความเร็ว",
    tierStd: `มาตรฐาน — ${product.turnaround}`, tierExp: `เร่งด่วน — ${SWORN_TURNAROUND_EXPRESS}`, tierPre: "Pre-check เอกสาร (ฟรี)",
    tierStdDesc: "ยอดนิยม — เหมาะกับส่วนใหญ่", tierExpDesc: "แปลด่วน + QA 2 ชั้น + Sworn stamp วันเดียว", tierPreDesc: "ตรวจสแกนก่อนเริ่มขั้นตอน — ไม่มีค่าใช้จ่าย",
    howTitle: "ขั้นตอนการทำ 4 ขั้น",
    faqTitle: "คำถามที่พบบ่อย",
    relatedTitle: "บริการที่เกี่ยวข้อง",
    breadcrumb: ["หน้าหลัก", "แปลสาบาน / Sworn Translator", "สินค้าและบริการ"],
    directAnswer: `฿${product.priceMin.toLocaleString()}–฿${product.priceMax.toLocaleString()} · ${product.turnaround} · แปลสาบานโดยนักแปล MoJ + เครือข่าย sworn translator ปลายทาง (DE/FR/ES/IT/NL/PL)`,
  } : {
    trust: `Certified by ${SWORN_LEAD_TRANSLATOR} + destination sworn-translator network`,
    priceLabel: "Fee per set",
    turnLabel: "Turnaround",
    acceptedBy: "Accepted by",
    langsTitle: "Top languages supported",
    usedFor: "Used for",
    whatIncluded: "Included in this fee",
    chainTitle: "The 4-step certification chain",
    tiers: "Service tiers",
    tierStd: `Standard — ${product.turnaround}`, tierExp: `Express — ${SWORN_TURNAROUND_EXPRESS}`, tierPre: "Pre-flight review (free)",
    tierStdDesc: "Most popular — right for most cases", tierExpDesc: "Same-day sworn stamp + two-pass QA", tierPreDesc: "Free scan review before we start — flags red flags before invoicing",
    howTitle: "How to book — 4 steps",
    faqTitle: "Frequently asked questions",
    relatedTitle: "Related services",
    breadcrumb: ["Home", "Sworn Translator", "Products"],
    directAnswer: `THB ${product.priceMin.toLocaleString()}–${product.priceMax.toLocaleString()} · ${product.turnaround} · Sworn translation by MoJ-registered Thai translator + destination sworn network (DE/FR/ES/IT/NL/PL).`,
  };

  const catalogPath = isTh ? "/sworn-translator/products" : "/en/sworn-translator/products";
  const hubPath = isTh ? "/sworn-translator" : "/en/sworn-translator";
  const chainIcons = [Languages, Stamp, Gavel, Award];

  return (
    <main className="min-h-screen bg-background pb-28 md:pb-16">
      <nav aria-label="Breadcrumb" className="mx-auto max-w-6xl px-4 pt-6 text-xs text-slate-500">
        <ol className="flex flex-wrap gap-1">
          <li><Link to={isTh ? "/" : "/en"} className="hover:text-blue-700">{L.breadcrumb[0]}</Link></li>
          <li>›</li>
          <li><Link to={hubPath} className="hover:text-blue-700">{L.breadcrumb[1]}</Link></li>
          <li>›</li>
          <li><Link to={catalogPath} className="hover:text-blue-700">{L.breadcrumb[2]}</Link></li>
          <li>›</li>
          <li aria-current="page" className="text-slate-700">{name}</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-6xl px-4 pt-6">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr]">
          <div>
            <div className="mb-3 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest">
              <span className="rounded-full bg-purple-100 px-3 py-1 text-purple-800">{catLabel}</span>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                <ShieldCheck className="mr-1 inline h-3 w-3" /> Sworn · MoJ
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">SKU {product.sku}</span>
            </div>
            <h1 className="font-display text-3xl font-extrabold leading-tight text-navy-deep sm:text-4xl lg:text-5xl">
              {isTh ? `แปลสาบาน — ${name}` : `Sworn Translation — ${name}`}
            </h1>
            <p data-speakable className="direct-answer mt-4 rounded-xl border-l-4 border-purple-400 bg-purple-50/60 p-4 text-[15px] leading-relaxed text-slate-700">
              {L.directAnswer}
            </p>

            <div className="price-band mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{L.priceLabel}</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-emerald-800">
                  THB {product.priceMin.toLocaleString()}–{product.priceMax.toLocaleString()}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? "คิดตามหน้า · ยืนยันหลังตรวจฟรี" : "Per page · confirmed after free review"}</div>
              </div>
              <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-purple-700">{L.turnLabel}</div>
                <div className="mt-1 flex items-center gap-2 font-display text-2xl font-extrabold text-purple-800">
                  <Clock className="h-5 w-5" /> {product.turnaround}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? `เร่งด่วน ${SWORN_TURNAROUND_EXPRESS}` : `Express ${SWORN_TURNAROUND_EXPRESS}`}</div>
              </div>
            </div>

            <div className="mt-5">
              <SwornTranslatorProductCTA product={product} locale={locale} variant="inline" />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              <ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />
              {L.trust}
            </div>
          </div>

          <aside className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-purple-50/40 p-6">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-purple-700">
              <Languages className="h-4 w-4" /> {L.langsTitle}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {langs.map((l) => (
                <span key={l} className="rounded-full border border-purple-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-purple-800">
                  {l}
                </span>
              ))}
            </div>
            <div className="mt-4 rounded-xl border-2 border-dashed border-purple-300 bg-white p-4">
              <div className="text-center">
                <div className="font-display text-3xl font-extrabold text-purple-800">20+</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-slate-600">
                  {isTh ? "ภาษาที่รองรับ" : "Languages covered"}
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  {isTh ? "DE/FR/ES/IT/NL/PL = sworn บังคับที่ปลายทาง" : "DE/FR/ES/IT/NL/PL = destination-sworn required"}
                </div>
              </div>
            </div>
            <ul className="mt-5 space-y-1.5 text-[12px] text-slate-700">
              <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5" /> {isTh ? "นักแปลจดทะเบียนกระทรวงยุติธรรมไทย" : "MoJ-registered Thai translator"}</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-purple-600 mt-0.5" /> {isTh ? "QA 2 ชั้น — translator → reviewer" : "Two-pass QA — translator → reviewer"}</li>
              <li className="flex gap-2"><Award className="h-3.5 w-3.5 text-purple-600 mt-0.5" /> {isTh ? "Bundle Notary + MFA + Apostille ได้" : "Bundle notary + MFA + Apostille"}</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.chainTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-4">
          {product.chainSteps.map((s, i) => {
            const Icon = chainIcons[i] || FileCheck2;
            return (
              <li key={s} className="relative rounded-2xl border border-purple-200 bg-gradient-to-br from-white to-purple-50/40 p-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-700 font-display text-sm font-bold text-white">{i + 1}</span>
                  <Icon className="h-5 w-5 text-purple-700" />
                </div>
                <div className="mt-2 text-[13px] font-semibold text-slate-800">{s}</div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.whatIncluded}</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {bullets.map((b) => (
            <li key={b} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <span className="text-sm text-slate-700">{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? `เกี่ยวกับแปลสาบาน ${name}` : `About sworn translation for ${name}`}
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{longDesc}</p>
        {product.doctype.useCases.length > 0 && (
          <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
            {isTh ? `กรณีที่ใช้บ่อย: ${product.doctype.useCases.join(" · ")}` : `Common uses: ${product.doctype.useCases.join(" · ")}`}
          </p>
        )}
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.tiers}</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            { label: L.tierPre, desc: L.tierPreDesc, price: 0, tone: "slate" },
            { label: L.tierStd, desc: L.tierStdDesc, price: product.priceMin, tone: "emerald", badge: isTh ? "แนะนำ" : "Popular" },
            { label: L.tierExp, desc: L.tierExpDesc, price: product.priceMax, tone: "purple" },
          ].map((t) => (
            <div key={t.label} className={`relative rounded-2xl border p-5 ${
              t.tone === "emerald" ? "border-emerald-300 bg-emerald-50/40 ring-2 ring-emerald-200"
              : t.tone === "purple" ? "border-purple-300 bg-purple-50/40"
              : "border-slate-300 bg-slate-50/40"
            }`}>
              {t.badge && (
                <span className="absolute -top-2 right-4 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                  {t.badge}
                </span>
              )}
              <div className="font-display text-lg font-extrabold text-navy-deep">{t.label}</div>
              <div className="mt-1 text-xs text-slate-600">{t.desc}</div>
              <div className="mt-3 font-display text-2xl font-extrabold text-navy-deep">
                {t.price === 0 ? (isTh ? "ฟรี" : "Free") : `THB ${t.price.toLocaleString()}`}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.acceptedBy}</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {accepted.map((a) => (
            <span key={a} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
              ✓ {a}
            </span>
          ))}
        </div>
        {product.doctype.useCases.length > 0 && (
          <>
            <h3 className="mt-6 font-display text-lg font-bold text-navy-deep">{L.usedFor}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {product.doctype.useCases.map((u) => (
                <span key={u} className="rounded-md bg-purple-50 px-2.5 py-1 text-[11px] font-semibold text-purple-800 ring-1 ring-purple-200">
                  {u}
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.howTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-2">
          {steps.map((s, i) => (
            <li key={s} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-700 font-display font-bold text-white">
                {i + 1}
              </span>
              <span className="text-sm text-slate-700">{s}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.faqTitle}</h2>
        <div className="mt-4 space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="speakable-faq group rounded-xl border border-slate-200 bg-white p-4 open:bg-purple-50/30">
              <summary className="cursor-pointer list-none font-display font-bold text-navy-deep">
                <span className="mr-2 text-purple-600 group-open:hidden">+</span>
                <span className="mr-2 hidden text-purple-600 group-open:inline">−</span>
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <CrossSellStrip source="sworn" locale={locale} />

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.relatedTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r) => (
            <Link
              key={r.sku}
              to={isTh ? "/sworn-translator/products/$slug" : "/en/sworn-translator/products/$slug"}
              params={{ slug: r.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-purple-400 hover:shadow-md"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-purple-700">
                {isTh ? r.categoryLabelTh : r.categoryLabelEn}
              </div>
              <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-purple-700">
                {isTh ? r.nameTh : r.nameEn}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                THB {r.priceMin.toLocaleString()}–{r.priceMax.toLocaleString()} · {r.turnaround}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-purple-700">
                {isTh ? "ดูรายละเอียด" : "View details"} <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <SwornTranslatorProductCTA product={product} locale={locale} variant="sticky-mobile" />
      <ProductDepthBlock cluster="sworn" locale={locale === "th" ? "th" : "en"} />
      <ConsultantAdvisoryBlock locale={locale === "th" ? "th" : "en"} topic="Sworn Translation" />
    </main>
  );
}
